import { z } from "zod";

export const ReportExtractionSchemaItem = z.object({
  schemaId: z.string(),
  name: z.string(),
  description: z.string(),
  version: z.number(),
});

export const ReportExtractionJobStatus = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
]);

/**
 * Human-readable label per job status, shared by every surface that shows one
 * so the wording cannot drift between the jobs list and the workpaper header.
 *
 * `pending` reads as "Pending Extraction" rather than a bare "Pending" because
 * it means the job is queued waiting for a free extraction worker slot — a
 * state a user can now sit in for a while, and one they should be able to tell
 * apart from an extraction that is actually underway.
 */
export const EXTRACTION_STATUS_LABELS: Record<
  z.infer<typeof ReportExtractionJobStatus>,
  string
> = {
  pending: "Pending Extraction",
  running: "Running",
  succeeded: "Complete",
  failed: "Failed",
};

/**
 * Label for a job status, preserving values this UI does not map.
 *
 * An unmapped status is capitalised rather than replaced with "Unknown": both
 * surfaces this replaced already did that (the header capitalised the raw
 * string, the jobs list leaned on CSS `capitalize`), and the backend types
 * `status` as a bare `str`, so `cancelled` and `timed_out` — which the sibling
 * session-run family already emits — would otherwise lose their outcome on the
 * screen meant to show it. "Unknown" is reserved for a genuinely absent status,
 * which is the loading state on the workpaper header.
 */
export const formatExtractionStatus = (status: string | null): string => {
  if (!status) return "Unknown";
  const parsed = ReportExtractionJobStatus.safeParse(status);
  if (parsed.success) return EXTRACTION_STATUS_LABELS[parsed.data];
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * The four statuses we render deliberately, widened to accept anything else.
 *
 * The backend types `status` as a bare `str` end to end — response model,
 * domain model, and the `update_extraction_job_status` writer — so a new value
 * can be introduced there without any type error, and the sibling session-run
 * model already ships `cancelled` and `timed_out`. Pinning this to the enum
 * would make one unrecognised job throw the whole list parse, which the caller
 * catches as a fetch failure: every extraction the user has would disappear
 * behind "No report extractions yet". Accepting the string instead degrades to
 * a capitalised label for that one row and leaves the rest intact.
 *
 * `string & {}` rather than a bare `string`: TypeScript collapses
 * `"pending" | ... | string` to plain `string`, which would silently drop the
 * compile-time guard on every literal comparison downstream — the poll filter,
 * the pulse check, and the Download gate's `status !== "succeeded"`. The
 * branded form keeps the four literals visible for autocomplete and for
 * non-overlapping-comparison errors while still accepting anything at runtime.
 */
const ReportExtractionJobStatusValue = z.union([
  ReportExtractionJobStatus,
  z.string() as unknown as z.ZodType<string & {}>,
]);

export const ReportExtractionJobResponse = z.object({
  jobId: z.string(),
  status: ReportExtractionJobStatusValue,
  schemaId: z.string(),
  schemaVersion: z.number(),
  provider: z.string(),
  documentId: z.string(),
  documentName: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string().optional(),
});

export const ReportExtractionItemCitation = z.object({
  id: z.string(),
  documentId: z.string(),
  encodedCitationString: z.string(),
  citationType: z.string(),
  pageNumber: z.number().nullable(),
  boundingBox: z.string().nullable(),
  pageWidth: z.number().nullable(),
  pageHeight: z.number().nullable(),
  shortSummary: z.string().nullable(),
  citationOrder: z.number(),
  // Which field on the parent item this citation sources. Null on items
  // that only carry item-level attribution (no per-field breakdown).
  fieldName: z.string().nullable().optional(),
});

export const ReportExtractionItem = z.object({
  id: z.string(),
  itemKey: z.string(),
  itemOrder: z.number(),
  data: z.record(z.unknown()),
  citations: z.array(ReportExtractionItemCitation),
});

export const SchemaStructure = z.object({
  scalarFields: z.array(z.string()),
  arrayFields: z.array(z.string()),
});

export const ReportExtractionItemsResponse = z.object({
  jobId: z.string(),
  schemaId: z.string(),
  schemaStructure: SchemaStructure.nullable(),
  items: z.array(ReportExtractionItem),
});

export type ReportExtractionSchemaItemType = z.infer<
  typeof ReportExtractionSchemaItem
>;
export type ReportExtractionJobStatusType = z.infer<
  typeof ReportExtractionJobStatus
>;
export type ReportExtractionJobResponseType = z.infer<
  typeof ReportExtractionJobResponse
>;
export type ReportExtractionItemCitationType = z.infer<
  typeof ReportExtractionItemCitation
>;
export type ReportExtractionItemType = z.infer<typeof ReportExtractionItem>;
export type SchemaStructureType = z.infer<typeof SchemaStructure>;
export type ReportExtractionItemsResponseType = z.infer<
  typeof ReportExtractionItemsResponse
>;
